import {useCallback, useEffect, useState} from 'react';
import type {RadioOption} from './types';

export interface UseRadioOptionsProps {
  options: RadioOption[];
  defaultValue?: RadioOption | RadioOption['value'];
  onChange?: (selectedOption: RadioOption) => void;
}

function getSelectedValue(
  options: RadioOption[],
  defaultValue?: RadioOption | RadioOption['value'],
) {
  return (
    (typeof defaultValue === 'string'
      ? options.find(option => option.value === defaultValue)
      : defaultValue) || ''
  );
}

export function useRadioOptions({options, defaultValue, onChange}: UseRadioOptionsProps) {
  const [selectedValue, setSelectedValue] = useState(() =>
    getSelectedValue(options, defaultValue),
  );

  useEffect(() => {
    setSelectedValue(getSelectedValue(options, defaultValue));
  }, [defaultValue, options]);

  const handleValueChange = useCallback(
    (value: RadioOption['value']) => {
      setSelectedValue(value);
      const selectedOption = options.find(option => option.value === value) || '';

      if (selectedOption && onChange) {
        onChange(selectedOption);
      }
    },
    [options, onChange],
  );

  return {
    selectedValue,
    handleValueChange,
  };
}
